import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPaperclip, FaPaperPlane, FaTrash, FaDownload, FaFile, FaImage, FaFilePdf, FaFileWord, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CrmTaskService, { TaskComment, TaskAttachment } from '../services/CrmTaskService';

interface TaskCommentsSectionProps {
  taskId: number;
}

export default function TaskCommentsSection({ taskId }: TaskCommentsSectionProps): React.JSX.Element {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [commentsData, attachmentsData] = await Promise.all([
        CrmTaskService.listComments(taskId),
        CrmTaskService.listAttachments(taskId),
      ]);
      setComments(commentsData);
      setAttachments(attachmentsData.filter(a => !a.comment));
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmitComment = async () => {
    const text = newComment.trim();
    if (!text || submitting) return;

    setSubmitting(true);
    try {
      await CrmTaskService.createComment(taskId, { text });
      setNewComment('');
      await loadData();
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Failed to create comment:', err);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await CrmTaskService.deleteComment(commentId);
      toast.success('Comment deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete comment:', err);
      toast.error('Failed to delete comment');
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      await CrmTaskService.uploadAttachment(taskId, file);
      toast.success('File uploaded');
      loadData();
    } catch (err) {
      console.error('Failed to upload file:', err);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm('Delete this file?')) return;

    try {
      await CrmTaskService.deleteAttachment(attachmentId);
      toast.success('File deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      toast.error('Failed to delete file');
    }
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <FaImage size={14} color="#7c3aed" />;
    if (contentType === 'application/pdf') return <FaFilePdf size={14} color="#dc2626" />;
    if (contentType.includes('word') || contentType.includes('document')) return <FaFileWord size={14} color="#2563eb" />;
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return <FaFileExcel size={14} color="#16a34a" />;
    return <FaFile size={14} color="#6b7280" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Comments List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 0',
          minHeight: '150px',
          maxHeight: '300px',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '24px' }}>
            Loading...
          </div>
        ) : comments.length === 0 && attachments.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '24px' }}>
            <p style={{ margin: 0, fontSize: '14px' }}>No comments yet</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#aaa' }}>
              Be the first to add a comment
            </p>
          </div>
        ) : (
          <>
            {/* Standalone Attachments */}
            {attachments.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    paddingLeft: '4px',
                  }}
                >
                  Attachments
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: '#f8f9fc',
                        borderRadius: '6px',
                        border: '1px solid #e0e3ef',
                        fontSize: '13px',
                      }}
                    >
                      {getFileIcon(attachment.content_type)}
                      <span style={{ color: '#021a4a', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {attachment.file_name}
                      </span>
                      <span style={{ color: '#9ca3af', fontSize: '11px' }}>
                        {formatFileSize(attachment.file_size)}
                      </span>
                      <a
                        href={attachment.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#7c3aed', padding: '4px' }}
                        title="Download"
                      >
                        <FaDownload size={12} />
                      </a>
                      <button
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          padding: '4px',
                        }}
                        title="Delete"
                      >
                        <FaTrash size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments */}
            {comments.map((comment) => (
              <div
                key={comment.id}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px 4px',
                  borderBottom: '1px solid #f0f1f5',
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#e0e7ff',
                    color: '#3730a3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(comment.author_name)}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#021a4a' }}>
                      {comment.author_name || 'Unknown'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {CrmTaskService.formatRelativeTime(comment.created_at)}
                    </span>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      style={{
                        marginLeft: 'auto',
                        background: 'none',
                        border: 'none',
                        color: '#9ca3af',
                        cursor: 'pointer',
                        padding: '4px',
                        opacity: 0.6,
                        transition: 'opacity 0.15s',
                      }}
                      title="Delete comment"
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.color = '#9ca3af'; }}
                    >
                      <FaTrash size={11} />
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#374151',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {comment.text}
                  </div>

                </div>
              </div>
            ))}
            <div ref={commentsEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div
        style={{
          borderTop: '1px solid #e0e3ef',
          padding: '16px 0 0 0',
        }}
      >
        <div style={{ position: 'relative' }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a comment... (Enter to send, Shift+Enter for new line)"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: '80px',
              border: '1px solid #e0e3ef',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'none',
              minHeight: '60px',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '8px',
              bottom: '8px',
              display: 'flex',
              gap: '4px',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
              style={{
                padding: '8px',
                background: 'none',
                border: 'none',
                color: uploadingFile ? '#ccc' : '#666',
                cursor: uploadingFile ? 'wait' : 'pointer',
                borderRadius: '4px',
              }}
              title="Attach file"
            >
              <FaPaperclip size={16} />
            </button>
            <button
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || submitting}
              style={{
                padding: '8px 12px',
                background: !newComment.trim() || submitting ? '#e0e3ef' : '#7c3aed',
                border: 'none',
                borderRadius: '6px',
                color: !newComment.trim() || submitting ? '#9ca3af' : 'white',
                cursor: !newComment.trim() || submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              <FaPaperPlane size={12} />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
